import jsPDF from 'jspdf';

// Baut den Lebenslauf im Layout der Webansicht (Seitenleiste links, Inhalt
// rechts), aber als Vektor-PDF mit echtem Text statt als Screenshot – so bleibt
// er für ATS lesbar. IDs entsprechen denen der <Hideable>-Elemente in
// Lebenslauf.js, ausgeblendete Elemente werden übersprungen.

const PAGE_W = 210;
const PAGE_H = 297;
const SIDEBAR_W = PAGE_W * 0.35;
const PAD_TOP = 12;
const PAD_BOTTOM = 12;

const ORANGE = [255, 140, 66];
const GREEN = [74, 124, 89];
const SIDEBAR_BG = [201, 109, 62];
const SIDEBAR_BOX = [206, 124, 81]; // 10 % Weiß auf Seitenleiste
const SIDEBAR_TRACK = [212, 138, 101]; // 20 % Weiß auf Seitenleiste

// Lädt das Porträt und schneidet es kreisrund zu (PNG mit Transparenz).
export async function loadCircularPhoto(src = '/img/portrait.png') {
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
  const size = 400;
  const side = Math.min(img.width, img.height);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size);
  return canvas.toDataURL('image/png');
}

export function buildCvPdf({ c, skillBars, address, hidden, photo }) {
  const show = (id) => !hidden.includes(id);
  const pdf = new jsPDF('p', 'mm', 'a4');

  const drawSidebar = () => {
    pdf.setFillColor(...SIDEBAR_BG);
    pdf.rect(0, 0, SIDEBAR_W, PAGE_H, 'F');
  };
  drawSidebar();

  const makeCol = (x, w) => ({ x, w, y: PAD_TOP, page: 1 });
  const left = makeCol(9, SIDEBAR_W - 18);
  const right = makeCol(SIDEBAR_W + 10, PAGE_W - SIDEBAR_W - 10 - 12);

  const lineHeight = (size) => size * 0.3528 * 1.4;

  // Stellt sicher, dass h mm in die Spalte passen, sonst auf die nächste Seite.
  const ensure = (col, h) => {
    if (col.y + h > PAGE_H - PAD_BOTTOM) {
      col.page += 1;
      while (pdf.getNumberOfPages() < col.page) {
        pdf.addPage();
        drawSidebar();
      }
      col.y = PAD_TOP;
    }
    pdf.setPage(col.page);
  };

  const write = (
    col,
    text,
    { size = 9, style = 'normal', color = [0, 0, 0], indent = 0, gap = 0, bullet = false, align = 'left' } = {}
  ) => {
    ensure(col, 0);
    pdf.setFont('helvetica', style);
    pdf.setFontSize(size);
    pdf.setTextColor(...color);
    const textIndent = bullet ? indent + 4.5 : indent;
    const lines = pdf.splitTextToSize(text, col.w - textIndent);
    const lh = lineHeight(size);
    lines.forEach((line, i) => {
      ensure(col, lh);
      pdf.setFont('helvetica', style);
      pdf.setFontSize(size);
      pdf.setTextColor(...color);
      const baseline = col.y + lh * 0.75;
      if (bullet && i === 0) {
        pdf.setFillColor(...GREEN);
        pdf.triangle(col.x + indent, baseline - 2.2, col.x + indent, baseline - 0.4, col.x + indent + 1.6, baseline - 1.3, 'F');
      }
      if (align === 'center') pdf.text(line, col.x + col.w / 2, baseline, { align: 'center' });
      else pdf.text(line, col.x + textIndent, baseline);
      col.y += lh;
    });
    col.y += gap;
  };

  const rule = (col, color, width) => {
    ensure(col, 1);
    pdf.setDrawColor(...color);
    pdf.setLineWidth(width);
    pdf.line(col.x, col.y, col.x + col.w, col.y);
  };

  // ---------- Linke Spalte ----------
  if (photo) {
    const d = 30;
    const cx = left.x + left.w / 2;
    pdf.addImage(photo, 'PNG', cx - d / 2, left.y, d, d);
    pdf.setDrawColor(226, 182, 158);
    pdf.setLineWidth(0.8);
    pdf.circle(cx, left.y + d / 2, d / 2, 'S');
    left.y += d + 6;
  }

  write(left, c.name, { size: 19, style: 'bold', color: [255, 255, 255], align: 'center', gap: 0.5 });
  write(left, c.title, { size: 11, color: [184, 212, 186], align: 'center', gap: 4 });

  const leftHeading = (text) => {
    ensure(left, 14);
    left.y += 2;
    write(left, text.toUpperCase(), { size: 9.5, style: 'bold', color: [255, 255, 255], gap: 0.5 });
    rule(left, GREEN, 0.5);
    left.y += 2.5;
  };

  if (show('summary')) {
    const size = 8.5;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(c.summary, left.w - 8);
    const boxH = lines.length * lineHeight(size) + 8;
    ensure(left, boxH);
    pdf.setFillColor(...SIDEBAR_BOX);
    pdf.roundedRect(left.x, left.y, left.w, boxH, 2, 2, 'F');
    const inner = { ...left, x: left.x + 4, w: left.w - 8, y: left.y + 4 };
    write(inner, c.summary, { size, color: [255, 255, 255] });
    left.y += boxH + 5;
  }

  const contact = [
    show('contact-address') && address,
    show('contact-phone') && '01727049258',
    show('contact-email') && 'Tavlin@hotmail.de',
    show('contact-birth') && c.birthDate,
  ].filter(Boolean);
  if (show('contact') && contact.length) {
    leftHeading(c.sections.contact);
    contact.forEach((line) => write(left, line, { size: 8.5, color: [255, 255, 255], gap: 1.5 }));
    left.y += 3;
  }

  if (show('skills')) {
    const skills = skillBars.filter((_, i) => show(`skill-${i}`));
    if (skills.length) {
      leftHeading(c.sections.skills);
      skills.forEach((skill) => {
        write(left, skill.name, { size: 8.5, color: [255, 255, 255], gap: 0.8 });
        ensure(left, 4);
        pdf.setFillColor(...SIDEBAR_TRACK);
        pdf.roundedRect(left.x, left.y, left.w, 1.8, 0.9, 0.9, 'F');
        pdf.setFillColor(...GREEN);
        pdf.roundedRect(left.x, left.y, (left.w * skill.level) / 100, 1.8, 0.9, 0.9, 'F');
        left.y += 4.2;
      });
      left.y += 3;
    }
  }

  if (show('tech')) {
    leftHeading(c.sections.tech);
    write(left, c.tech, { size: 8.5, color: [255, 255, 255], gap: 3 });
  }

  if (show('languages')) {
    const langs = c.languages.filter((_, i) => show(`lang-${i}`));
    if (langs.length) {
      leftHeading(c.sections.languages);
      langs.forEach((lang) => {
        const lh = lineHeight(8.5);
        ensure(left, lh);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(255, 255, 255);
        pdf.text(lang.name, left.x, left.y + lh * 0.75);
        pdf.text(lang.level, left.x + left.w, left.y + lh * 0.75, { align: 'right' });
        left.y += lh + 1.5;
      });
    }
  }

  // ---------- Rechte Spalte ----------
  const sectionHeader = (text) => {
    ensure(right, 22);
    right.y += 4;
    write(right, text.toUpperCase(), { size: 14, style: 'bold', color: ORANGE, gap: 1 });
    rule(right, GREEN, 0.8);
    right.y += 4;
  };

  const bullets = (prefix, list) => {
    list.forEach((b, i) => {
      if (show(`${prefix}-b${i}`)) write(right, b, { indent: 1, gap: 1.2, bullet: true });
    });
  };

  const separator = () => {
    right.y += 1;
    rule(right, [238, 238, 238], 0.25);
    right.y += 5;
  };

  const entry = (title, sub, duration, prefix, list, isLast) => {
    ensure(right, 24);
    write(right, title, { size: 13, style: 'bold', color: ORANGE, gap: 0.5 });
    write(right, sub, { size: 10.5, color: [102, 102, 102], gap: 0.5 });
    write(right, duration, { size: 9, style: 'italic', color: [136, 136, 136], gap: 2 });
    bullets(prefix, list);
    if (!isLast) separator();
  };

  if (show('experience')) {
    write(right, c.sections.experience, { size: 19, style: 'bold', color: ORANGE, align: 'center', gap: 6 });
    const jobs = c.experience.map((job, i) => ({ job, i })).filter(({ i }) => show(`exp-${i}`));
    jobs.forEach(({ job, i }, n) =>
      entry(job.role, job.company, job.duration, `exp-${i}`, job.bullets, n === jobs.length - 1)
    );
  }

  if (show('education')) {
    sectionHeader(c.sections.education);
    const edus = c.education.map((edu, i) => ({ edu, i })).filter(({ i }) => show(`edu-${i}`));
    edus.forEach(({ edu, i }, n) =>
      entry(edu.degree, edu.institution, edu.duration, `edu-${i}`, edu.bullets, n === edus.length - 1)
    );
  }

  if (show('additional')) {
    sectionHeader(c.sections.additional);
    if (show('additional-school')) {
      write(right, c.sections.schoolEducation, { size: 10.5, style: 'bold', color: ORANGE, gap: 1 });
      c.additional.schoolEducation.forEach((line) => write(right, line, { color: [102, 102, 102], gap: 0.5 }));
      right.y += 4;
    }
    if (show('additional-early')) {
      write(right, c.sections.earlyExperience, { size: 10.5, style: 'bold', color: ORANGE, gap: 1 });
      c.additional.earlyExperience.forEach((line) => write(right, line, { color: [102, 102, 102], gap: 0.5 }));
    }
  }

  return pdf;
}
